/**
 * ประลอง (packages/shared systems/duelMatch.ts, docs/research/C0_PVP.md): the duel's screens, in the duel clone.
 * - How a bout starts: click a character or the rival (the target frame shows), right-click the face in the frame: a
 *   menu (ประลอง, วิชาของคู่ต่อสู้, ดูข้อมูล, ส่งข้อความ, เพิ่มเพื่อน). A challenge from another player is answered in a
 *   slim strip at the top (รับคำท้า / ปฏิเสธคำท้า).
 * - The bout: both พลังชีวิต and พลังกาย bars (the stamina is what a block spends, so a block can be read), the rounds
 *   won, the round clock, the countdown and round banners, and a one-line tell of what the other is doing (a ท่าทำลาย
 *   wind-up, a block, a stagger): the triangle made readable. A counter flashes, rings and kicks the screen; the guard bars (แต้มการ์ด) show how close a guard is to breaking.
 * - The ring on the ground (render/passes/duelRing.ts), the sounds (sfx/sounds/duel.ts), the result card with the
 *   bout's tally (blows landed, blocked, guards broken, counters).
 * Against the stand-in it reads the bout's state; between two players (a server) the same screen is built from the
 * `pvpDuel` events. It sends commands (`duelChallenge`, `duelAnswer`, `duelMatch`); nothing here decides a rule.
 */
import {
  combatBlockText,
  DUEL_MATCH,
  duelMatchOf,
  duelRingOutOf,
  type BoutStats,
  type Entity,
  type MatchState,
  type PlayerCommand,
  type World,
  type WorldEvent,
} from '@nine/shared';
import { duelRing } from '../render/passes/duelRing';
import { settings } from '../settings';
import { playSfx } from '../sfx';
import { h } from './dom';
import { lifeMenuItems } from './lifeGift';
import './duelHudCompact'; // phone top bar (CSS only)
import { DuelRingOutHud } from './duelRingOutHud';

const setLabel = (el: HTMLElement, text: string): void => {
  if (el.textContent !== text) el.textContent = text;
};

const CSS = `
.duel-ui{position:fixed;inset:0;pointer-events:none;z-index:38;font-family:inherit;color:#f4f1ea}
.duel-ui [hidden]{display:none!important}
.duel-ui button{pointer-events:auto;cursor:pointer}
#hotbar button.queued{outline:2px solid #ffe9a8;outline-offset:-2px;animation:duelqueued .7s ease-in-out infinite alternate}
@keyframes duelqueued{from{box-shadow:0 0 0 rgba(255,233,168,0)}to{box-shadow:0 0 12px rgba(255,233,168,.9)}}
#target .tlock{background:#ffe9a8;color:#2a1a0a;font-size:11px;font-weight:700;padding:0 6px;border-radius:4px;margin-right:6px}
/* DUEL-RING r3: no quest pointer in a bout; short windows drop the vitals panel and the hint strip and shrink the banner (note for ns-uxui) */
body.duel-on #csGuide{display:none!important}
@media (max-height:500px){.duel-result{bottom:8px;right:8px;width:min(250px,34vw);padding:8px 12px}.duel-result h2{font-size:20px;letter-spacing:1px}.duel-result p{margin:3px 0 6px;font-size:12px}.duel-result button{font-size:12px;padding:4px 10px}body.duel-on #vitals{display:none!important}.duel-ui .duel-legend{display:none!important}.duel-banner{top:104px;font-size:30px;letter-spacing:1px}.duel-banner small{font-size:13px;margin-top:0}}
body.duel-on #target,body.duel-on #chat,body.duel-on #rightcol,body.duel-on #status,body.duel-on #party{display:none!important}
#target{padding-left:58px!important}
.tg-gp{display:flex;gap:6px;align-items:center;width:100%;font-size:11px;color:#d4cec3}
.tg-gp .g-track{flex:1}
.tg-gp .g-track i{background:#6fd0e8}
.tg-face{position:absolute;left:8px;top:50%;transform:translateY(-50%);width:42px;height:42px;image-rendering:pixelated;border:1px solid rgba(244,241,234,.3);border-radius:6px;background:rgba(0,0,0,.4);cursor:context-menu;pointer-events:auto}
.duel-top{position:absolute;top:12px;left:50%;transform:translateX(-50%);width:min(760px,94vw);display:grid;grid-template-columns:1fr 112px 1fr;gap:10px;align-items:start}
.duel-side{background:rgba(22,19,15,.82);border:1px solid rgba(244,241,234,.22);border-radius:8px;padding:6px 10px 7px}
.duel-side b{font-size:14px;font-weight:600}
.duel-side small{font-size:12px;color:#d4cec3;margin-left:6px}
.duel-bar{position:relative;height:13px;margin-top:4px;background:rgba(0,0,0,.5);border:1px solid rgba(244,241,234,.2);overflow:hidden}
.duel-bar i{position:absolute;left:0;top:0;bottom:0}
.duel-bar .lag{background:#f4f1ea;opacity:.85}
.duel-bar .hp{background:#e2503f}
.duel-gp{position:relative;height:7px;margin-top:3px;background:rgba(0,0,0,.5);border:1px solid rgba(244,241,234,.16);overflow:hidden}
.duel-gp i{display:block;height:100%;background:#6fd0e8}
.duel-rage{position:relative;height:6px;margin-top:3px;background:rgba(0,0,0,.5);border:1px solid rgba(244,241,234,.16);overflow:hidden}
.duel-rage i{display:block;height:100%;background:#b08ae0;width:0}
.duel-foe .duel-rage i{margin-left:auto}
.duel-rage.full{border-color:#e4c4ff;animation:duelRageFull .5s steps(2) infinite}
.duel-rage.full i{background:#f0d8ff}
@keyframes duelRageFull{50%{border-color:#7a52b0}}
.duel-gp.broken i{background:#8c8579}
.duel-gp b{position:absolute;inset:0;font-size:9px;line-height:7px;text-align:center;color:#f4f1ea;font-weight:600;text-shadow:0 1px 0 #000}
.duel-foe .duel-gp i{margin-left:auto}
.duel-stam{height:4px;margin-top:3px;background:rgba(0,0,0,.5)}
.duel-stam i{display:block;height:100%;background:#dcc56c}
.duel-foe .duel-bar i{left:auto;right:0}
.duel-foe{text-align:right}
.duel-mid{text-align:center;background:rgba(22,19,15,.82);border:1px solid rgba(244,241,234,.22);border-radius:8px;padding:5px 4px}
.duel-mid .clock{font-size:20px;font-weight:700;letter-spacing:1px}
.duel-mid .round{font-size:12px;color:#d4cec3}
.duel-pips{display:flex;justify-content:center;gap:5px;margin-top:3px}
.duel-pips span{width:11px;height:11px;border-radius:50%;border:1px solid #f4d08c;background:transparent}
.duel-pips span.on{background:#f4d08c}
.duel-tell{position:absolute;top:84px;left:50%;transform:translateX(-50%);padding:5px 14px;border-radius:6px;font-size:14px;font-weight:600;background:rgba(22,19,15,.88);border:1px solid rgba(244,241,234,.3);white-space:nowrap}
.duel-tell.red{border-color:#d6412f;color:#ffb4a8}
.duel-tell.amber{border-color:#f6c46c;color:#f6c46c}
.duel-tell.green{border-color:#7ee0b2;color:#7ee0b2}
.duel-banner{position:absolute;top:34%;left:0;right:0;text-align:center;font-size:64px;font-weight:800;text-shadow:0 3px 0 #000,0 0 18px rgba(0,0,0,.7);letter-spacing:2px}
.duel-banner small{display:block;font-size:20px;font-weight:600;letter-spacing:0;margin-top:4px;color:#f4d08c}
.duel-banner.pop{animation:duelpop .35s ease-out}
@keyframes duelpop{from{transform:scale(1.5);opacity:0}to{transform:scale(1);opacity:1}}
.duel-parry{position:absolute;top:27%;left:0;right:0;text-align:center;font-size:44px;font-weight:800;color:#f4d08c;text-shadow:0 3px 0 #000,0 0 22px rgba(244,208,140,.8);opacity:0}
.duel-parry.on{animation:duelparry .8s ease-out}
@keyframes duelparry{0%{transform:scale(1.7);opacity:0}15%{transform:scale(1);opacity:1}70%{opacity:1}100%{transform:translateY(-14px);opacity:0}}
.duel-flash{position:absolute;inset:0;background:radial-gradient(ellipse at center,rgba(244,208,140,.0) 40%,rgba(244,208,140,.45) 100%);opacity:0}
.duel-flash.on{animation:duelflash .35s ease-out}
@keyframes duelflash{0%{opacity:1}100%{opacity:0}}
.duel-legend{position:absolute;bottom:176px;left:50%;transform:translateX(-50%);font-size:12px;color:#d4cec3;background:rgba(22,19,15,.7);padding:3px 10px;border-radius:6px;white-space:nowrap}
.duel-quit{position:absolute;top:12px;right:12px;font-size:12px;padding:4px 10px;background:rgba(22,19,15,.82);color:#d4cec3;border:1px solid rgba(244,241,234,.22);border-radius:6px}
.duel-invite{position:absolute;top:12px;left:50%;transform:translateX(-50%);display:flex;gap:10px;align-items:center;background:rgba(22,19,15,.88);border:1px solid #8a6d45;border-radius:8px;padding:6px 12px;font-size:14px;pointer-events:auto}
.duel-invite b{color:#f4d08c}
.duel-menu{position:fixed;min-width:210px;background:rgba(22,19,15,.94);border:1px solid #8a6d45;border-radius:8px;padding:4px;pointer-events:auto;z-index:60}
.duel-menu .hd{padding:4px 10px 6px;font-size:12px;color:#d4cec3;border-bottom:1px solid rgba(244,241,234,.16);margin-bottom:3px}
.duel-menu button{display:block;width:100%;text-align:left;background:transparent;border:0;color:#f4f1ea;font-size:14px;padding:6px 10px;border-radius:5px}
.duel-menu button:hover:not(:disabled){background:rgba(124,38,32,.7)}
.duel-menu button:disabled{color:#8c8579;cursor:default}
.duel-menu small{display:block;font-size:11px;color:#b5ad9d}
.duel-invite button,.duel-result button{font-size:14px;padding:7px 16px;border-radius:6px;border:1px solid #8a6d45;background:#7c2620;color:#f4f1ea}
.duel-invite button.ghost,.duel-result button.ghost{background:transparent}
.duel-result{position:absolute;right:14px;bottom:96px;transform:none;width:min(300px,42vw);background:rgba(22,19,15,.94);border:1px solid #8a6d45;border-radius:12px;padding:16px 18px;text-align:center;pointer-events:auto}
.duel-result h2{margin:0;font-size:28px;letter-spacing:2px}
.duel-result h2.win{color:#f4d08c}.duel-result h2.lose{color:#ef7a6a}
.duel-result p{margin:6px 0 8px;font-size:14px;color:#d4cec3}
.duel-tally{display:grid;grid-template-columns:1fr auto 1fr;gap:2px 10px;margin:4px 0 12px;font-size:13px}
.duel-tally span:nth-child(3n+1){text-align:right}.duel-tally span:nth-child(3n){text-align:left}
.duel-tally span:nth-child(3n+2){color:#d4cec3}
.duel-result .row{display:flex;gap:10px;justify-content:center}
`;

const TXT = {
  challenge: 'ท้าประลอง',
  closer: 'เดินเข้าไปใกล้อีกนิด',
  giveUp: 'ยอมแพ้',
  again: 'สู้อีกครั้ง (Enter)',
  leave: 'กลับ',
  win: 'ชนะ',
  lose: 'แพ้',
  accept: 'รับคำท้า',
  decline: 'ปฏิเสธคำท้า',
};

/** Who can be challenged instead, when the target cannot be (the hub rival's name). */
const rivalHint = (w: World): string => {
  const r = w.get(duelMatchOf(w)?.rivalId ?? -1);
  return r ? ` · ลองท้า ${r.name} (${Math.round(Math.hypot(r.x - w.player.x, r.y - w.player.y))} ม.)` : '';
};
const STARS = (n: number) => '★'.repeat(n + 1) + '☆'.repeat(Math.max(0, 4 - n));

/** What the screen shows, from the local bout's state or, between two players, from the events. */
interface View {
  meId: number;
  foeId: number;
  meName: string;
  foeName: string;
  phase: 'countdown' | 'wait' | 'fight' | 'roundEnd' | 'result';
  /** The rival's stars (its real level, 0-4) in a local bout. */
  stars: number | null;
  paused: boolean;
  /** The KO beat (phase roundEnd, 0.5 s): the banner waits for it. */
  ko: boolean;
  rank: { from: number; to: number; streak: number } | null;
  /** Seconds left in the phase (a guess from the events between players). */
  t: number;
  round: number;
  wins: [number, number];
  lastRound: 0 | 1 | -1 | null;
  winnerMe: boolean | null;
  sub: string;
  stats: [BoutStats, BoutStats] | null;
  pvp: boolean;
  parryN: number;
  parryMe: boolean;
  /** แต้มการ์ด [me, the rival]: 0-1 shares, seconds of broken guard; stance seconds left. */
  gp: [number, number] | null;
  broken: [number, number];
  stance: [number, number];
  ring: [number, number, number] | null;
  record: string;
}

interface Remote {
  a: number;
  b: number;
  names: [string, string];
  phase: View['phase'];
  round: number;
  wins: [number, number];
  ring: [number, number, number] | null;
  winner: number | null | undefined;
  last: number | null | undefined;
  at: number;
  parryN: number;
  parryBy: number;
  gp: [number, number] | null;
  broken: [number, number];
  /** Stances: the ids that opened one and when it runs out (this HUD's clock). */
  stance: Map<number, number>;
  guardAt: number;
}

export class DuelHud {
  private readonly root: HTMLElement;
  private readonly top: HTMLElement;
  private readonly meName: HTMLElement;
  private readonly foeNameEl: HTMLElement;
  private readonly meLag: HTMLElement;
  private readonly meHp: HTMLElement;
  private readonly foeLag: HTMLElement;
  private readonly foeHp: HTMLElement;
  private readonly meGp: { el: HTMLElement; i: HTMLElement; label: HTMLElement };
  private readonly foeGp: { el: HTMLElement; i: HTMLElement; label: HTMLElement };
  private readonly meRage: HTMLElement;
  private readonly foeRage: HTMLElement;
  private readonly meStam: HTMLElement;
  private readonly foeStam: HTMLElement;
  private readonly clock: HTMLElement;
  private readonly roundEl: HTMLElement;
  private readonly pipsMe: HTMLElement;
  private readonly pipsFoe: HTMLElement;
  private readonly tell: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly parry: HTMLElement;
  private readonly flash: HTMLElement;
  private readonly legend: HTMLElement;
  private readonly quit: HTMLButtonElement;
  private readonly inviteEl: HTMLElement;
  private readonly inviteName: HTMLElement;
  private menu: HTMLElement | null = null;
  /** The target the open menu is for: the menu closes when the target changes or is gone. */
  private menuFor = -1;
  private readonly result: HTMLElement;
  private readonly resultTitle: HTMLElement;
  private readonly resultText: HTMLElement;
  private readonly tally: HTMLElement;
  private readonly resultBtns: HTMLButtonElement[];
  private readonly lag = new Map<string, number>();
  private bannerKey = '';
  private invite: { from: string; until: number } | null = null;
  private remote: Remote | null = null;
  private clockS = 0;
  /** The out-of-ring count under the top bar (ui/duelRingOutHud.ts), and the fight clock last seen (a clock-out result). */
  private readonly ringOut = new DuelRingOutHud();
  private lastFightT = 99;
  private lastParry = 0;
  private calloutsWas: boolean | null = null;
  private foeStance = false;
  private foeBroken = false;
  private meBroken = false;
  private lastPhaseKey = '';
  private foeWas = { stun: false, block: false };
  private soundAt = 0;

  constructor(
    private readonly world: World,
    private readonly send: (c: PlayerCommand) => void,
    private readonly fx?: { impact(col: string, flash: number, punch: number, shake: number): void },
    private readonly toast: (text: string) => void = () => undefined,
  ) {
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.append(style);
    const bar = () => {
      const lag = h('i', { class: 'lag' });
      const hp = h('i', { class: 'hp' });
      return { el: h('div', { class: 'duel-bar' }, lag, hp), lag, hp };
    };
    const gpBar = () => {
      const i = h('i');
      const label = h('b');
      return { el: h('div', { class: 'duel-gp', title: 'แต้มการ์ด' }, i, label), i, label };
    };
    const stam = () => {
      const i = h('i');
      return { el: h('div', { class: 'duel-stam' }, i), i };
    };
    const rageBar = () => {
      const i = h('i');
      return { el: h('div', { class: 'duel-rage', title: 'ความโกรธ (ท่าใหญ่ใช้เต็มหลอด)' }, i), i };
    };
    const mr = rageBar();
    const fr = rageBar();
    this.meRage = mr.el;
    this.foeRage = fr.el;
    const mb = bar();
    const fb = bar();
    const ms = stam();
    const fs = stam();
    const mgp = gpBar();
    const fgp = gpBar();
    this.meGp = mgp;
    this.foeGp = fgp;
    this.meName = h('b');
    this.foeNameEl = h('b');
    this.meLag = mb.lag;
    this.meHp = mb.hp;
    this.foeLag = fb.lag;
    this.foeHp = fb.hp;
    this.meStam = ms.i;
    this.foeStam = fs.i;
    this.clock = h('div', { class: 'clock' });
    this.roundEl = h('div', { class: 'round' });
    this.pipsMe = h('div', { class: 'duel-pips' });
    this.pipsFoe = h('div', { class: 'duel-pips' });
    this.top = h(
      'div',
      { class: 'duel-top', hidden: true },
      h('div', { class: 'duel-side' }, this.meName, mb.el, mgp.el, ms.el, mr.el),
      h('div', { class: 'duel-mid' }, this.roundEl, this.clock, this.pipsMe),
      h('div', { class: 'duel-side duel-foe' }, this.foeNameEl, fb.el, fgp.el, fs.el, fr.el, this.pipsFoe),
    );
    this.tell = h('div', { class: 'duel-tell', hidden: true });
    this.banner = h('div', { class: 'duel-banner', hidden: true });
    this.parry = h('div', { class: 'duel-parry', textContent: 'สวนกลับ!' });
    this.flash = h('div', { class: 'duel-flash' });
    this.legend = h('div', {
      class: 'duel-legend',
      hidden: true,
      textContent:
        'ตั้งรับ (F) ลดความเสียหายครึ่งหนึ่ง แต่โดนอัดมากการ์ดแตก · แดง = ท่าทำลาย (ห้ามตั้งรับ) · ส้ม ฟ้า เหลือง = ตั้งรับหรือหลบ · ม่วง = ท่าพลัง หลบเท่านั้น (Q) · ท่าสวนกลับ (7)',
    });
    this.quit = h('button', { class: 'duel-quit', type: 'button', hidden: true, textContent: TXT.giveUp });
    this.quit.addEventListener('click', () => this.send({ t: 'duelMatch', op: 'giveUp' }));
    this.inviteName = h('b');
    const yes = h('button', { type: 'button', textContent: TXT.accept });
    const no = h('button', { type: 'button', class: 'ghost', textContent: TXT.decline });
    yes.addEventListener('click', () => {
      this.send({ t: 'duelAnswer', accept: true });
      this.invite = null;
    });
    no.addEventListener('click', () => {
      this.send({ t: 'duelAnswer', accept: false });
      this.invite = null;
    });
    this.inviteEl = h('div', { class: 'duel-invite', hidden: true }, this.inviteName, ' ท้าประลอง ', yes, no);
    this.resultTitle = h('h2');
    this.resultText = h('p');
    this.tally = h('div', { class: 'duel-tally' });
    const again = h('button', { type: 'button', textContent: TXT.again });
    const leave = h('button', { type: 'button', class: 'ghost', textContent: TXT.leave });
    again.addEventListener('click', () => this.send({ t: 'duelMatch', op: 'rematch' }));
    leave.addEventListener('click', () => this.send({ t: 'duelMatch', op: 'leave' }));
    this.resultBtns = [again, leave];
    this.result = h(
      'div',
      { class: 'duel-result', hidden: true },
      this.resultTitle,
      this.resultText,
      this.tally,
      h('div', { class: 'row' }, again, leave),
    );
    this.root = h(
      'div',
      { class: 'duel-ui' },
      this.flash,
      this.top,
      this.tell,
      this.banner,
      this.parry,
      this.legend,
      this.quit,
      this.inviteEl,
      this.result,
    );
    document.body.append(this.root);
    this.root.append(this.ringOut.el); // the out-of-ring count (one-bout format)
    window.addEventListener('keydown', (e) => this.onKey(e));
    this.onTargetChange(() => this.closeMenu());
    // Right-click the face in the target frame: the menu.
    document.querySelector('#target')?.addEventListener('contextmenu', (e) => this.openMenu(e as MouseEvent));
    window.addEventListener('pointerdown', (e) => {
      if (this.menu && !this.menu.contains(e.target as Node)) this.closeMenu();
    });
  }

  /** Events: a challenge from another player, and the stages of a bout between two players (a server's). */
  handle(ev: WorldEvent): void {
    if (ev.type !== 'pvpDuel') return;
    const me = this.you;
    if (ev.phase === 'invite' && ev.b === me) this.invite = { from: ev.names[0], until: this.clockS + 20 };
    else if (ev.phase === 'declined' || ev.phase === 'countdown') this.invite = null;
    // A bout the local system is not running (a server's): the events tell where it stands.
    if (duelMatchOf(this.world)?.current) return;
    const r = this.remote;
    const base: Remote = r && r.a === ev.a && r.b === ev.b ? r : { a: ev.a, b: ev.b, names: ev.names, phase: 'countdown', round: 1, wins: [0, 0], ring: null, winner: undefined, last: undefined, at: this.clockS, parryN: 0, parryBy: -1, gp: null, broken: [0, 0], stance: new Map(), guardAt: 0 };
    if (ev.round !== undefined) base.round = ev.round;
    if (ev.wins) base.wins = ev.wins;
    if (ev.ring) base.ring = ev.ring;
    if (ev.phase === 'guard' && ev.gp) {
      base.gp = ev.gp;
      base.broken = ev.broken ?? [0, 0];
      base.guardAt = this.clockS;
    } else if (ev.phase === 'stance') {
      base.stance.set(ev.parry ?? -1, this.clockS + (ev.window ?? 1));
    } else if (ev.phase === 'counter') {
      base.parryN++;
      base.parryBy = ev.parry ?? -1;
      base.stance.delete(ev.parry ?? -1);
    } else if (ev.phase === 'countdown' || ev.phase === 'fight' || ev.phase === 'result' || ev.phase === 'roundEnd') {
      base.phase = ev.phase;
      base.at = this.clockS;
      if (ev.phase === 'roundEnd') base.last = ev.winner;
      if (ev.phase === 'result') base.winner = ev.winner;
      if (ev.phase === 'countdown') base.winner = undefined;
    } else if (ev.phase === 'declined') {
      this.remote = null;
      return;
    }
    this.remote = base;
  }

  private onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') this.closeMenu();
    // A key that can change the target (Tab, a skill): look again right after the world has seen it.
    else if (this.menu) requestAnimationFrame(() => this.watchTarget());
    if (e.key !== 'Enter' || e.repeat) return;
    const t = e.target as HTMLElement | null;
    if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
    if (!this.result.hidden && !this.resultBtns[0]!.hidden) this.send({ t: 'duelMatch', op: 'rematch' });
  }

  private closeMenu(): void {
    this.menu?.remove();
    this.menu = null;
  }

  private lastTarget: number | null = null;
  private readonly targetHooks: Array<(id: number | null) => void> = [];
  /** Calls `fn` whenever the player's target changes by any path (click, Tab, auto retarget, death, clear). */
  onTargetChange(fn: (id: number | null) => void): void {
    this.targetHooks.push(fn);
  }

  /** The target-change check: the right-click menu belongs to one target and closes when it changes or the frame hides. */
  private watchTarget(): void {
    const id = this.world.player.targetId ?? null;
    if (id !== this.lastTarget) {
      this.lastTarget = id;
      for (const fn of this.targetHooks) fn(id);
    }
    if (this.menu) {
      const frame = document.querySelector<HTMLElement>('#target');
      if (id !== this.menuFor || !this.world.get(this.menuFor) || !frame || frame.hidden) this.closeMenu();
    }
  }

  /** The context menu of the target (right-click on its face): what can be done with them. */
  private openMenu(ev: MouseEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.closeMenu();
    const w = this.world;
    const t = w.get(w.player.targetId);
    if (!t) return;
    const sys = duelMatchOf(w);
    const rival = !!sys && t.id === sys.rivalId;
    const person = rival || (t.kind === 'player' && t.id !== w.primaryId && t.id !== this.you);
    const d = Math.hypot(t.x - w.player.x, t.y - w.player.y);
    const busy = !!this.viewOf();
    let why = '';
    if (!person) why = `ท้าประลองคนนี้ไม่ได้${rivalHint(w)}`;
    else if (busy) why = 'อยู่ระหว่างประลองอยู่แล้ว';
    else if (d > (rival ? DUEL_MATCH.reach : 12)) why = 'อยู่ไกลเกินไป';
    else if (w.player.combatT > 0) why = combatBlockText(w.player);
    const item = (label: string, on: () => void, off = '', sub = ''): HTMLButtonElement => {
      const b = h('button', { type: 'button', disabled: !!off }, label, sub || off ? h('small', { textContent: off || sub }) : null);
      b.addEventListener('click', () => {
        this.closeMenu();
        on();
      });
      return b;
    };
    const items: HTMLElement[] = [
      item(TXT.challenge, () => this.send({ t: 'duelChallenge', id: t.id }), why, rival ? `${sys!.nextStyle.name} · ${sys!.nextArt} · ฝีมือ ${STARS(sys!.nextLevel)}` : ''),
    ];
    if (rival)
      items.push(
        item(
          sys!.mirror ? 'วิชาคู่ต่อสู้: เหมือนของเรา' : 'วิชาคู่ต่อสู้: ของเขาเอง',
          () => this.send({ t: 'duelMatch', op: 'mirror' }),
          busy ? 'อยู่ระหว่างประลองอยู่แล้ว' : '',
          'กดเพื่อสลับ',
        ),
      );
    // The townsfolk of the plains: gifts (ui/lifeGift.ts).
    items.push(...lifeMenuItems(w, t, this.send, item, () => this.closeMenu(), { x: ev.clientX, y: ev.clientY }));
    items.push(
      item('ดูข้อมูล', () => this.toast(`${t.name || 'ผู้เล่นอื่น'} · ระดับ ${t.level}`)),
      item('ส่งข้อความ', () => this.toast('ส่งข้อความหาผู้เล่นยังไม่เปิดใช้')),
      item('เพิ่มเพื่อน', () => this.toast('ระบบเพื่อนยังไม่เปิดใช้')),
    );
    const m = h('div', { class: 'duel-menu' }, h('div', { class: 'hd', textContent: t.name || 'ผู้เล่นอื่น' }), ...items);
    m.style.left = `${Math.min(ev.clientX, window.innerWidth - 230)}px`;
    m.style.top = `${Math.min(ev.clientY, window.innerHeight - 200)}px`;
    document.body.append(m);
    this.menu = m;
    this.menuFor = t.id;
  }

  private bar(e: Entity | undefined, hp: HTMLElement, lag: HTMLElement, stam: HTMLElement, key: string, dt: number): void {
    if (!e) return;
    const r = Math.max(0, Math.min(1, e.hp / e.maxHp));
    const prev = this.lag.get(key) ?? r;
    // The white chip eases down after a hit and jumps up when health is restored.
    const l = r >= prev ? r : Math.max(r, prev - dt * 0.35);
    this.lag.set(key, l);
    hp.style.width = `${(r * 100).toFixed(1)}%`;
    lag.style.width = `${(l * 100).toFixed(1)}%`;
    stam.style.width = `${Math.max(0, Math.min(1, e.stamina / e.maxStamina)) * 100}%`;
  }

  private say(text: string, sub = '', key = text): void {
    if (this.bannerKey === key) return;
    this.bannerKey = key;
    this.banner.hidden = !text;
    this.banner.replaceChildren(text, ...(sub ? [h('small', { textContent: sub })] : []));
    this.banner.classList.remove('pop');
    void this.banner.offsetWidth;
    this.banner.classList.add('pop');
  }

  private sfx(id: string): void {
    playSfx(id);
  }

  /** This player's id on the server (a replica's own character is the local player), else the local one. */
  private get you(): number {
    return (window as unknown as { __net?: { you: number | null } }).__net?.you ?? this.world.primaryId;
  }

  /** The entity for an id as the screen sees it: the player's own body is the local player. */
  private body(id: number): Entity | undefined {
    return id === this.you ? this.world.player : this.world.get(id);
  }

  /** The bout as the screen shows it, or null when none. */
  private viewOf(): View | null {
    const w = this.world;
    const sys = duelMatchOf(w);
    const s: Readonly<MatchState> | null = sys?.current ?? null;
    if (s) {
      const rec = sys?.record;
      return {
        meId: s.meId,
        foeId: s.foeId,
        meName: s.names[0],
        foeName: s.names[1],
        phase: s.phase,
        ko: s.ko === true,
        paused: s.paused,
        stars: s.pvp ? null : s.level,
        rank: s.rank,
        t: s.t,
        round: s.round,
        wins: [s.wins[0], s.wins[1]],
        lastRound: s.lastRound,
        winnerMe: s.winner === null || s.winner === -1 ? null : s.winner === 0,
        sub: s.pvp ? '' : `${s.style.name} · ${s.foeArt}`,
        stats: s.stats,
        pvp: s.pvp,
        parryN: s.counter?.n ?? 0,
        parryMe: s.counter?.who === 0,
        gp: [s.guard[0].gp / s.guard[0].max, s.guard[1].gp / s.guard[1].max],
        broken: [s.guard[0].brokenT, s.guard[1].brokenT],
        stance: [s.guard[0].counterT, s.guard[1].counterT],
        ring: [s.ring.x, s.ring.y, s.ring.r],
        record: rec && !s.pvp ? '' : '',
      };
    }
    const r = this.remote;
    if (!r) return null;
    if (r.phase === 'result' && this.clockS - r.at > 6.5) return null;
    const meIsA = r.a === this.you;
    const meId = meIsA ? r.a : r.b;
    const foeId = meIsA ? r.b : r.a;
    const el = this.clockS - r.at;
    const last = r.last === undefined ? null : r.last === null ? -1 : r.last === meId ? 0 : 1;
    return {
      meId,
      foeId,
      meName: r.names[meIsA ? 0 : 1],
      foeName: r.names[meIsA ? 1 : 0],
      phase: r.phase,
      paused: false,
      ko: false,
      stars: null,
      rank: null,
      t: r.phase === 'countdown' ? Math.max(0, (r.round > 1 ? DUEL_MATCH.countdownNext : DUEL_MATCH.countdownFirst) - el) : r.phase === 'fight' ? Math.max(0, DUEL_MATCH.roundSeconds - el) : 0,
      round: r.round,
      wins: meIsA ? [r.wins[0], r.wins[1]] : [r.wins[1], r.wins[0]],
      lastRound: last as View['lastRound'],
      winnerMe: r.winner == null ? null : r.winner === meId,
      sub: '',
      stats: null,
      pvp: true,
      parryN: r.parryN,
      parryMe: r.parryBy === meId,
      gp: r.gp ? (meIsA ? [r.gp[0] / 100, r.gp[1] / 100] : [r.gp[1] / 100, r.gp[0] / 100]) : null,
      broken: meIsA ? [r.broken[0], r.broken[1]] : [r.broken[1], r.broken[0]],
      stance: [Math.max(0, (r.stance.get(meId) ?? 0) - this.clockS), Math.max(0, (r.stance.get(foeId) ?? 0) - this.clockS)],
      ring: r.ring,
      record: '',
    };
  }

  /** The target frame's guard bar: for a fighter (the rival, another player) their guard points. */
  private syncTargetGuard(): void {
    const box = document.querySelector<HTMLElement>('#tgpbox');
    const bar = document.querySelector<HTMLElement>('#tgp i');
    if (!box || !bar) return;
    const w = this.world;
    const t = w.get(w.player.targetId);
    const sys = duelMatchOf(w);
    const fighter = !!t && ((!!sys && t.id === sys.rivalId) || (t.kind === 'player' && t.id !== w.primaryId));
    box.hidden = !fighter;
    if (fighter) bar.style.width = '100%';
  }

  sync(dt: number): void {
    this.clockS += dt;
    this.watchTarget();
    this.syncTargetGuard();
    const w = this.world;
    const v = this.viewOf();
    document.body.classList.toggle('duel-on', !!v);
    if (v) document.getElementById('sceneRecap')?.remove(); // the opening's recap card never stays over a bout
    duelRing.t = this.clockS;
    if (!v) {
      duelRing.on = false;
      duelRing.me = duelRing.foe = -1;
      if (this.calloutsWas !== null) {
        settings.callouts = this.calloutsWas;
        this.calloutsWas = null;
      }
      this.top.hidden = true;
      this.tell.hidden = true;
      this.legend.hidden = true;
      this.quit.hidden = true;
      this.banner.hidden = true;
      this.bannerKey = '';
      this.result.hidden = true;
      this.lastPhaseKey = '';
      this.syncPrompt();
      return;
    }
    this.syncBout(v, dt);
    void w;
  }

  /** One guard-point bar: the share of points, greyed with the seconds left while the guard is broken. */
  private guardBar(g: { el: HTMLElement; i: HTMLElement; label: HTMLElement }, share: number | undefined, broken: number): void {
    const on = broken > 0;
    g.el.classList.toggle('broken', on);
    g.i.style.width = `${on ? 100 : Math.max(0, Math.min(1, share ?? 1)) * 100}%`;
    setLabel(g.label, on ? `การ์ดแตก ${broken.toFixed(1)}` : '');
  }

  private syncBout(v: View, dt: number): void {
    const me = this.body(v.meId);
    const foe = this.body(v.foeId);
    duelRing.me = me?.id ?? -1;
    duelRing.foe = foe?.id ?? -1;
    this.top.hidden = false;
    // The moves' names floating over the fighters are off in a bout: the tell is read once, at the foe.
    if (this.calloutsWas === null) {
      this.calloutsWas = settings.callouts;
      settings.callouts = false;
    }
    // Between two players the replica has no พลังกาย of anyone's (not in the snapshot): the bars are left out.
    for (const el of [this.meStam, this.foeStam]) (el.parentElement as HTMLElement).style.display = v.pvp ? 'none' : '';
    this.meName.textContent = v.meName;
    this.foeNameEl.textContent = v.stars === null ? v.foeName : `${v.foeName} ${STARS(v.stars)}`;
    this.bar(me, this.meHp, this.meLag, this.meStam, 'me', dt);
    this.bar(foe, this.foeHp, this.foeLag, this.foeStam, 'foe', dt);
    // Both fighters' rage (the ultimate costs a full bar): a pulse when full. Not in a bout between players (not replicated).
    for (const [e, el] of [[me, this.meRage], [foe, this.foeRage]] as const) {
      el.style.display = v.pvp || !e ? 'none' : '';
      if (!e || !e.maxRage) continue;
      const k = Math.max(0, Math.min(1, e.rage / e.maxRage));
      (el.firstElementChild as HTMLElement).style.width = `${(k * 100).toFixed(1)}%`;
      el.classList.toggle('full', k >= 0.999);
    }
    this.guardBar(this.meGp, v.gp?.[0], v.broken[0]);
    this.guardBar(this.foeGp, v.gp?.[1], v.broken[1]);
    duelRing.stance = v.stance;
    duelRing.broken = v.broken;
    // The one-bout format (DUEL_FORMAT): no rounds to show, a 2:00 clock.
    const rounds = DUEL_MATCH.winRounds > 1;
    const mmss = (t: number) => (DUEL_MATCH.roundSeconds >= 60 ? `${Math.floor(Math.ceil(t) / 60)}:${String(Math.ceil(t) % 60).padStart(2, '0')}` : String(Math.ceil(t)));
    this.roundEl.textContent = rounds ? `ยกที่ ${v.round}` : '';
    this.clock.textContent = v.phase === 'fight' ? mmss(v.t) : v.phase === 'countdown' || v.phase === 'wait' ? mmss(DUEL_MATCH.roundSeconds) : '';
    if (v.phase === 'fight') this.lastFightT = v.t;
    this.ringOut.sync(this.world, me?.id ?? null, foe?.id ?? null, v.phase === 'fight' && !v.paused);
    const key = `${v.wins[0]}${v.wins[1]}`;
    if (this.pipsMe.dataset.k !== key) {
      this.pipsMe.dataset.k = key;
      const pips = (el: HTMLElement, n: number) =>
        el.replaceChildren(...Array.from({ length: DUEL_MATCH.winRounds > 1 ? DUEL_MATCH.winRounds : 0 }, (_, i) => h('span', { class: i < n ? 'on' : '' })));
      pips(this.pipsMe, v.wins[0]);
      pips(this.pipsFoe, v.wins[1]);
    }
    this.legend.hidden = v.phase === 'result';
    this.quit.hidden = v.phase === 'result' || v.pvp;
    // (The tell is at the foe, once: ring and word on the ground view; no line at the top.)
    this.tell.hidden = true;

    // The ring on the ground, glowing when a fighter is near its edge.
    if (v.ring) {
      Object.assign(duelRing, { on: true, x: v.ring[0], y: v.ring[1], r: v.ring[2] });
      const near = (e?: Entity) => (e ? Math.hypot(e.x - v.ring![0], e.y - v.ring![1]) / v.ring![2] : 0);
      duelRing.edge = Math.max(0, Math.min(1, (Math.max(near(me), near(foe)) - 0.82) / 0.18));
    }

    // Sounds and the banners, once per change.
    const pk = `${v.phase}${v.ko ? 'k' : ''}${v.round}${v.phase === 'countdown' ? Math.ceil(v.t) : ''}`;
    const changed = pk !== this.lastPhaseKey;
    if (changed) {
      this.lastPhaseKey = pk;
      if (v.phase === 'countdown') this.sfx('duel.tick');
      else if (v.phase === 'fight') this.sfx('duel.go');
      else if (v.phase === 'roundEnd' && !v.ko) this.sfx(v.lastRound === 0 ? 'duel.roundWin' : v.lastRound === 1 ? 'duel.roundLose' : 'duel.tick');
      else if (v.phase === 'result') this.sfx(v.winnerMe ? 'duel.win' : 'duel.lose');
    }
    if (v.phase === 'wait') this.say('พร้อมแล้ว', 'ขยับหรือกดท่าเพื่อเริ่ม', 'wait');
    else if (v.paused) this.say('หยุดชั่วคราว', 'ขยับหรือกดท่าเพื่อสู้ต่อ', 'paused');
    else if (v.phase === 'countdown') {
      const n = Math.ceil(v.t);
      const rd = DUEL_MATCH.winRounds > 1 ? `ยกที่ ${v.round}` : '';
      this.say(String(Math.max(1, n)), [rd, v.sub].filter(Boolean).join(' · '), `c${v.round}-${n}`);
    } else if (v.phase === 'fight') {
      const k = `f${v.round}`;
      if (this.bannerKey !== k && this.bannerKey !== `${k}x`) this.say('สู้!', '', k);
      else if (v.t < DUEL_MATCH.roundSeconds - 0.8 && this.bannerKey === k) {
        this.banner.hidden = true;
        this.bannerKey = `${k}x`;
      }
    } else if (v.phase === 'roundEnd' && v.ko) {
      this.banner.hidden = true;
      this.bannerKey = '';
    } else if (v.phase === 'roundEnd') {
      const t = v.lastRound === 0 ? 'ชนะยกนี้' : v.lastRound === 1 ? 'แพ้ยกนี้' : 'เสมอ';
      this.say(t, `${v.wins[0]} - ${v.wins[1]}`, `e${v.round}`);
    } else {
      this.banner.hidden = true;
      this.bannerKey = '';
    }

    // A parry: a flash, a word, a ring of the bell and a kick of the screen.
    if (v.parryN !== this.lastParry) {
      if (v.parryN > this.lastParry) {
        this.parry.textContent = v.parryMe ? 'สวนกลับ!' : 'ถูกสวนกลับ';
        for (const el of [this.parry, this.flash]) {
          el.classList.remove('on');
          void el.offsetWidth;
          el.classList.add('on');
        }
        this.sfx('duel.parry');
        this.fx?.impact('244,208,140', v.parryMe ? 0.5 : 0.25, 0.6, v.parryMe ? 5 : 3);
      }
      this.lastParry = v.parryN;
    }
    // The other fighter's openings and guards, by ear.
    const stun = !!foe && foe.stunT > 0.3;
    const block = !!foe?.blocking;
    if (v.phase === 'fight' && this.clockS - this.soundAt > 0.4) {
      if (stun && !this.foeWas.stun) {
        this.sfx('duel.opening');
        this.soundAt = this.clockS;
      } else if (block && !this.foeWas.block) {
        this.sfx('duel.guardUp');
        this.soundAt = this.clockS;
      }
    }
    this.foeWas = { stun, block };
    if (v.stance[1] > 0 && !this.foeStance) this.sfx('duel.stance');
    this.foeStance = v.stance[1] > 0;
    if (v.broken[1] > 0 && !this.foeBroken) this.sfx('duel.guardShatter');
    this.foeBroken = v.broken[1] > 0;
    if (v.broken[0] > 0 && !this.meBroken) this.sfx('duel.guardShatter');
    this.meBroken = v.broken[0] > 0;

    this.result.hidden = v.phase !== 'result';
    if (v.phase === 'result') {
      const won = !!v.winnerMe;
      const draw = v.winnerMe === null;
      this.resultTitle.textContent = draw ? 'เสมอ' : won ? TXT.win : TXT.lose;
      this.resultTitle.className = won ? 'win' : 'lose';
      const rk = v.rank;
      const stars = (n: number) => '★'.repeat(n + 1) + '☆'.repeat(Math.max(0, 4 - n));
      // One bout: how it ended (out of the ring / the clock / a fall) in place of the round score.
      const outLoser = duelRingOutOf(this.world)?.lastLoser ?? null;
      const how = outLoser !== null ? (outLoser === v.meId ? 'ท่านอยู่นอกลานเกิน 10 วินาที' : 'คู่ต่อสู้อยู่นอกลานเกิน 10 วินาที') : this.lastFightT <= 0.3 ? 'หมดเวลา' : 'ล้มลง';
      this.resultText.textContent = `${DUEL_MATCH.winRounds > 1 ? `${v.wins[0]} - ${v.wins[1]}` : how} · ${v.foeName}${rk ? ` · ${v.sub.split(' · ')[0]} ${stars(rk.from)} → ${stars(rk.to)}${rk.streak > 1 ? ` · ชนะติดกัน ${rk.streak}` : ''}` : ''}`;
      for (const b of this.resultBtns) b.hidden = v.pvp;
      const tb = duelMatchOf(this.world)?.current?.tourney;
      if (tb) {
        this.resultBtns[0]!.hidden = true;
        this.resultBtns[1]!.textContent = 'ตกลง';
        this.resultText.textContent = `${won ? 'ชนะ' : 'แพ้'}นัดนี้ ${v.wins[0]}–${v.wins[1]} · ${v.foeName} · ${tb.label}`;
      } else this.resultBtns[1]!.textContent = TXT.leave;
      const st = v.stats;
      const sig = st ? JSON.stringify(st) : '';
      if (this.tally.dataset.k !== sig) {
        this.tally.dataset.k = sig;
        this.tally.replaceChildren(
          ...(st
            ? ([
                ['ตีโดน', 'hits'],
                ['ตั้งรับได้', 'blocked'],
                ['การ์ดแตก', 'broken'],
                ['สวนกลับ', 'counters'],
              ] as const).flatMap(([label, k]) => [
                h('span', { textContent: String(st[0][k]) }),
                h('span', { textContent: label }),
                h('span', { textContent: String(st[1][k]) }),
              ])
            : []),
        );
      }
    }
  }

  private syncPrompt(): void {
    duelRing.on = false;
    const w = this.world;
    const sys = duelMatchOf(w);
    const r = sys && sys.rivalId >= 0 ? w.get(sys.rivalId) : undefined;
    duelRing.rival = r ? r.id : -1;
    duelRing.rivalNear = !!r && w.player.targetId === r.id && Math.hypot(r.x - w.player.x, r.y - w.player.y) <= DUEL_MATCH.reach * 1.5;
    const on = !!this.invite && this.clockS < this.invite.until;
    if (!on) this.invite = null;
    this.inviteEl.hidden = !on;
    if (on) this.inviteName.textContent = this.invite!.from;
  }
}
